//////////////////////////////////////////////////////////
// Source: https://gist.github.com/banksean/300494
//////////////////////////////////////////////////////////

/*
 I've wrapped Makoto Matsumoto and Takuji Nishimura's code in a namespace
 so it's better encapsulated. Now you can have multiple random number generators
 and they won't stomp all over eachother's state.
 If you want to use this as a substitute for Math.random(), use the random()
 method like so:
 var m = new MersenneTwister();
 var randomNumber = m.random();
 You can also call the other genrand_{foo}() methods on the instance.

 If you want to use a specific seed in order to get a repeatable random
 sequence, pass an integer into the constructor:

 var m = new MersenneTwister(123);

 and that will always produce the same random sequence.

 Sean McCullough (banksean@gmail.com)
 */

/*
 A C-program for MT19937, with initialization improved 2002/1/26.
 Coded by Takuji Nishimura and Makoto Matsumoto.
 Before using, initialize the state by using initGenRand(seed)
 or init_by_array(init_key, key_length).
 Copyright (C) 1997 - 2002, Makoto Matsumoto and Takuji Nishimura,
 All rights reserved.
 Redistribution and use in source and binary forms, with or without
 modification, are permitted provided that the following conditions
 are met:
 1. Redistributions of source code must retain the above copyright
 notice, this list of conditions and the following disclaimer.
 2. Redistributions in binary form must reproduce the above copyright
 notice, this list of conditions and the following disclaimer in the
 documentation and/or other materials provided with the distribution.
 3. The names of its contributors may not be used to endorse or promote
 products derived from this software without specific prior written
 permission.
 THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR
 A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR
 CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,
 PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR
 PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
 NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 Any feedback is very welcome.
 http://www.math.sci.hiroshima-u.ac.jp/~m-mat/MT/emt.html
 email: m-mat @ math.sci.hiroshima-u.ac.jp (remove space)
 */
 export default class MersenneTwister {
    /**
     * 
     * @param {number} seed 
     */
    constructor(seed) {
        /**
         * @private
         * @constant
         * @type {number}
         */
        this.N = 624;
        /**
         * @private
         * @constant
         * @type {number}
         */
        this.M = 397;
        /**
         * @private
         * @constant
         * @type {number}
         */
        this.MATRIX_A = 0x9908b0df;  // constant vector a
        /**
         * @private
         * @constant
         * @type {number}
         */
        this.UPPER_MASK = 0x80000000;  // most significant w-r bits
        /**
         * @private
         * @constant
         * @type {number}
         */
        this.LOWER_MASK = 0x7fffffff;  // least significant r bits

        /**
         * @private
         * @constant
         * @type {number}
         */
        this.randFactor = 1.0 / 4294967296.0;


        /**
         * @private
         * @constant
         * @type {Array.<number>}
         */
        this.mag01 = [0x0, this.MATRIX_A];

        /**
         * The array for the state vector
         * @constant
         * @private
         * @type {Array.<number>}
         */
        this.mt = new Array(this.N);

        /**
         * @private
         * @type {number}
         */
        this.mti = this.N + 1; // mti==N+1 means mt[N] is not initialized

        ////this.initGenRand2(seed);

        this.setSeed(seed);
    }

    /**
     * @param {number} seed 
     */
    setSeed(seed) {
        // reset mti
        this.mti = this.N + 1; // mti==N+1 means mt[N] is not initialized
        this.initGenRand2(seed);
    }

    /**
     * Initializes mt[N] with a seed
     * @this {MersenneTwister}
     * @param {number} s
     */
    initGenRand2(s) {
        this.mt[0]= s & 0xffffffff;
        for (this.mti = 1; this.mti < this.N; this.mti++) {
            this.mt[this.mti] = (1812433253 * (this.mt[this.mti-1] ^ (this.mt[this.mti-1] >> 30)) + this.mti);
            /* See Knuth TAOCP Vol2. 3rd Ed. P.106 for multiplier. */
            /* In the previous versions, MSBs of the seed affect   */
            /* only MSBs of the array mt[].                        */
            /* 2002/01/09 modified by Makoto Matsumoto             */
            this.mt[this.mti] &= 0xffffffff;
            /* for >32 bit machines */
        }
    }

    ///**
    // * Initializes mt[N] with a seed
    // * @this {MersenneTwister}
    // * @param {number} s
    // */
    //initGenRand(s) {
    //    "use strict";
    //    this.mt[0] = s >>> 0;
    //    var xxx;
    //    for (this.mti = 1; this.mti < this.N; this.mti++) {
    //        xxx = this.mt[this.mti - 1] ^ (this.mt[this.mti - 1] >>> 30);
    //        this.mt[this.mti] = (((((xxx & 0xffff0000) >>> 16) * 1812433253) << 16)
    //            + (xxx & 0x0000ffff) * 1812433253) + this.mti;
    //
    //        /* See Knuth TAOCP Vol2. 3rd Ed. P.106 for multiplier. */
    //        /* In the previous versions, MSBs of the seed affect */
    //        /* only MSBs of the array mt[]. */
    //        /* 2002/01/09 modified by Makoto Matsumoto */
    //        this.mt[this.mti] >>>= 0;
    //        /* for >32 bit machines */
    //    }
    //}

    /**
     * Generates a random number on [0,0xffffffff]-interval
     * @private
     * @this {MersenneTwister}
     * @returns {number}
     */
    genRandInt() {
        var y;

        //var mag01 = new Array(0x0, this.MATRIX_A);
        //var mag01 = [0x0, this.MATRIX_A];
        this.mag01[0] = 0x0;
        this.mag01[1] = this.MATRIX_A;

        /* mag01[x] = x * MATRIX_A for x=0,1 */

        if (this.mti >= this.N) { /* generate N words at one time */
            var kk;

            for (kk = 0; kk < this.N - this.M; kk++) {
                y = (this.mt[kk] & this.UPPER_MASK) | (this.mt[kk + 1] & this.LOWER_MASK);
                this.mt[kk] = this.mt[kk + this.M] ^ (y >>> 1) ^ this.mag01[y & 0x1];
            }
            for (; kk < this.N - 1; kk++) {
                y = (this.mt[kk] & this.UPPER_MASK) | (this.mt[kk + 1] & this.LOWER_MASK);
                this.mt[kk] = this.mt[kk + (this.M - this.N)] ^ (y >>> 1) ^ this.mag01[y & 0x1];
            }
            y = (this.mt[this.N - 1] & this.UPPER_MASK) | (this.mt[0] & this.LOWER_MASK);
            this.mt[this.N - 1] = this.mt[this.M - 1] ^ (y >>> 1) ^ this.mag01[y & 0x1];

            this.mti = 0;
        }

        y = this.mt[this.mti++];

        /* Tempering */
        y ^= (y >>> 11);
        y ^= (y << 7) & 0x9d2c5680;
        y ^= (y << 15) & 0xefc60000;
        y ^= (y >>> 18);

        return y >>> 0;
    }

    /**
     * Generates a random number on [0,1)-real-interval.
     * @this {MersenneTwister}
     * @returns {number}
     */
    random() {
        return this.genRandInt() * this.randFactor;  // divided by 2^32
    }

    /**
     * Returns a random integer in the range of 0 to limit (exclusive).
     * @param limit The max value of the resulting integer (exclusive).
     * @returns {number}
     */
    randomInt(limit) {
        return (this.random() * limit) | 0;
    }

}